<template>
  <section
    v-if="faqData?.length"
    class="container mx-auto py-6 md:pt-10 md:pb-15"
  >
    <h2 class="text-center section-heading mb-8 md:mb-12">
      {{ title }}
    </h2>
    <div
      class="flex flex-col justify-center items-center w-[90%] md:w-2/3 mx-auto gap-6"
    >
      <template v-if="isLoading">
        <div v-for="i in 3" :key="i" class="flex w-full">
          <div class="ml-4 flex-1">
            <UtilsSkeletonLoading class="w-full mb-2 h-7" />
            <UtilsSkeletonLoading class="w-full h-24" />
          </div>
        </div>
      </template>
      <template v-else>
        <ul class="w-full">
          <li
            v-for="(faq, index) in faqData"
            :key="index"
            class="cursor-pointer"
            @click="openCloseAccordion(index)"
          >
            <div
              class="faq-title last:border-r-0 border-opacity-80 p-3 flex justify-between relative items-center"
              :class="
                activeAccordion === index ? 'pb-0' : 'border-b border-bw-200'
              "
            >
              <h3 class="text-lg font-medium mb-0 pr-3">
                {{ faq.attributes?.question }}
              </h3>
              <Transition name="spin">
                <span
                  v-show="activeAccordion === index"
                  class="text-primary-primary text-2xl toggle-icon"
                  >-</span
                >
              </Transition>
              <Transition name="spin">
                <span
                  v-show="activeAccordion !== index"
                  class="text-primary-primary text-2xl toggle-icon"
                  >+</span
                >
              </Transition>
            </div>
            <Transition name="slide-fadetop">
              <div
                v-show="activeAccordion === index"
                class="text-sm text-bw-900 answer overflow-hidden mt-2 border-b border-bw-200 pb-6"
                v-html="faq.attributes?.answer"
              ></div>
            </Transition>
          </li>
        </ul>
      </template>
    </div>
    <div
      v-if="faqCtaData?.attributes?.title"
      class="mt-8 flex justify-center items-center"
    >
      <NuxtLink
        :to="`/ideas/${faqCtaData?.attributes?.target?.data?.attributes?.slug}`"
        title="Culture"
        class="py-3 px-[10px] sm:px-4 sm:py-3 text-sm sm:text-base border border-[#FF6700] rounded-md text-[#FF6700] block w-max hover:bg-[#FF6700] hover:text-white mx-auto md:mx-0"
        >{{ faqCtaData?.attributes?.title || 'Read More' }}</NuxtLink
      >
    </div>
  </section>
</template>
<script setup>
const props = defineProps({
  title: {
    type: String,
    default: 'Your Curiosity Matters, We Have Answers'
  },
  type: {
    type: String,
    default: 'career'
  },
  componentId: {
    type: Number,
    default: undefined
  }
})

const activeAccordion = ref(0)

const openCloseAccordion = index => {
  if (activeAccordion.value === index) activeAccordion.value = -1
  else if (activeAccordion.value !== index) activeAccordion.value = index
}

const isLoading = ref(true)

const faqData = ref([])
const faqCtaData = ref({})

const getFaq = async () => {
  const { data, error, pending } = await useSuitmediaApiData(`/api/faqs`, {
    query: {
      'fields[0]': 'id',
      'fields[1]': 'question',
      'fields[2]': 'answer',
      'filters[type]': props.type,
      component_ids: props.componentId
    },
    retry: 3,
    retryDelay: 300
  })

  isLoading.value = pending.value

  if (error.value) {
    throw createError({
      statusCode: 404,
      message: `FAQ does not exist`
    })
  }

  return data.value
}

const getFaqCta = async () => {
  const { data, error, pending } = await useSuitmediaApiData(`/api/faq-ctas`, {
    query: {
      'fields[0]': 'id',
      'fields[1]': 'type',
      'fields[2]': 'title',
      'fields[3]': 'target',
      'filters[type]': props.type,
      component_ids: props.componentId,
      populate: 'target'
    },
    retry: 3,
    retryDelay: 300
  })

  isLoading.value = pending.value

  if (error.value) {
    return {
      data: []
    }
  }

  return data.value
}

const [faqs, faqCta] = await Promise.all([getFaq(), getFaqCta()])

faqData.value = faqs.data
faqCtaData.value = faqCta.data?.[0] ?? {}
</script>

<style lang="scss" scoped>
:deep(.answer) {
  ul {
    list-style-type: disc;
    font-size: 1rem;
    line-height: 1.5rem;
    margin-left: 32px;

    li {
      margin-bottom: 0.5rem;
    }
  }
  p {
    color: #8f8f8f;
    margin-top: 0.5rem;
    margin-left: 0.5rem;
  }
}

.toggle-icon {
  position: absolute;
  right: 0;
}

.slide-fadetop-enter-active {
  transition: all 0.3s ease;
}

.slide-fadetop-leave-active {
  transition: all 0.3s ease;
}

.slide-fadetop-enter-from {
  opacity: 0;
  transform: translateY(-100px);
}
.slide-fadetop-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.spin-enter-active,
.spin-leave-active {
  transition: all 0.2s ease-in-out;
}

.spin-enter-from,
.spin-leave-to {
  opacity: 0;
  transform: rotate(180deg);
}
</style>
